import { Brand, prettyPrintBrand } from '../context/claims';
import { danishCountryLocalization, danishLocalization } from './texts/danish';
import { englishCountryLocalization, englishLocalization } from './texts/english';
import { finnishCountryLocalization, finnishLocalization } from './texts/finnish';
import { norwegianCountryLocalization, norwegianLocalization } from './texts/norwegian';
import { type CountryLocalizations, type TextLocalization, Locale } from './types';

export type TextFetcher = (key: keyof TextLocalization, ...arg: string[]) => string;

function NewFetcher(texts: TextLocalization, brand: Brand): TextFetcher {
  return (key, ...args) => {
    const text = texts[key];
    if (text && typeof text === 'string') {
      const brandedText = text.replace('{marketBrandName}', prettyPrintBrand(brand));
      return args.reduce((acc, curr, i) => acc.replace(`{${i}}`, curr), brandedText);
    }
    return text;
  };
}

export const TextFetcherBuilder: (
  locale: Locale,
  brand: Brand
) => { fetcher: TextFetcher; countryLocalization: CountryLocalizations } = (locale, brand) => {
  const texts = LocaleMap[locale];
  return {
    fetcher: NewFetcher(texts, brand),
    countryLocalization: CountryLocaleMap[locale],
  };
};

const LocaleMap: { [key in Locale]: TextLocalization } = {
  [Locale.EN]: englishLocalization,
  [Locale.NO]: norwegianLocalization,
  [Locale.FI]: finnishLocalization,
  [Locale.DK]: danishLocalization,
};

const CountryLocaleMap: { [key in Locale]: CountryLocalizations } = {
  [Locale.EN]: englishCountryLocalization,
  [Locale.NO]: norwegianCountryLocalization,
  [Locale.FI]: finnishCountryLocalization,
  [Locale.DK]: danishCountryLocalization,
};

export const Locales: Locale[] = Object.keys(LocaleMap) as Locale[];

export function localeLabel(
  locale: Locale,
  texts: CountryLocalizations
): { short: string; long: string } {
  switch (locale) {
    case Locale.EN:
      return { short: 'EN', long: texts.britain.language };
    case Locale.NO:
      return { short: 'NO', long: texts.norway.language };
    case Locale.FI:
      return { short: 'FI', long: texts.finland.language };
    case Locale.DK:
      return { short: 'DK', long: texts.denmark.language };
  }
}

const toSupportedLocale = (language: string | null | undefined) => {
  switch (language?.split('-')[0].toLowerCase()) {
    case 'no':
    case 'nn':
    case 'nb':
      return Locale.NO;
    case 'fi':
      return Locale.FI;
    case 'da':
      return Locale.DK;
    case 'en':
      return Locale.EN;
    default:
      return null;
  }
};

export const tryGetSupportedBrowserLocale = () => toSupportedLocale(navigator.language);

// The Widget SDK forwards a merchant-set language override as ?language=, which must win over the
// browser language.
const tryGetLanguageOverrideLocale = () =>
  toSupportedLocale(new URLSearchParams(window.location.search).get('language'));

export const CurrentLocale: Locale =
  tryGetLanguageOverrideLocale() ?? tryGetSupportedBrowserLocale() ?? Locale.EN;
